<!--
	Spinning ring with an optional status label. `decorative` drops the label and role
	for spinners whose surrounding text already announces the state (e.g. a busy button).
-->
<script lang="ts">
	interface Props {
		text?: string;
		/** sm sits inline with text (buttons, status lines), lg fills a page or panel. */
		size?: 'sm' | 'lg';
		/** Ring colour: brand accent, the surrounding text colour, or white for imagery. */
		tone?: 'accent' | 'current' | 'inverse';
		/** Lays the ring and label out in a row instead of filling the container. */
		inline?: boolean;
		decorative?: boolean;
	}

	const {
		text = 'Loading...',
		size = 'lg',
		tone = 'accent',
		inline = false,
		decorative = false
	}: Props = $props();

	const labelId = $props.id();
</script>

{#if decorative}
	<span class="ring {size} {tone}" aria-hidden="true"></span>
{:else}
	<div role="status" class="container" class:inline aria-labelledby={labelId}>
		<span class="ring {size} {tone}" aria-hidden="true"></span>
		<span id={labelId}>{text}</span>
	</div>
{/if}

<style>
	.container {
		display: flex;
		flex-direction: column-reverse;
		justify-content: center;
		align-items: center;
		gap: var(--space-4);
		height: 100%;

		&.inline {
			flex-direction: row;
			justify-content: flex-start;
			gap: var(--space-2);
			height: auto;
		}
	}

	.ring {
		display: block;
		flex: none;
		animation: spin var(--spin-duration) linear infinite;
		border: 0.2rem solid currentColor;
		border-right-color: transparent;
		border-radius: var(--radius-full);

		&.sm {
			border-width: 0.15rem;
			width: var(--icon-sm);
			height: var(--icon-sm);
		}

		&.lg {
			width: calc(var(--icon-lg) * 2);
			height: calc(var(--icon-lg) * 2);
		}

		&.accent {
			color: var(--accent-text);
		}

		&.inverse {
			color: var(--text-on-image);
		}
	}
</style>
